import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, ArrowRight, Heart, ShieldCheck, Sparkles, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Hero } from "@/components/Hero";
import { ServicesGrid } from "@/components/ServicesGrid";
import { Testimonials } from "@/components/Testimonials";
import { FAQ } from "@/components/FAQ";
import { CountUp, FadeUp } from "@/components/Motion";
import { buildMeta, buildLinks, faqSchema } from "@/lib/seo";
import { FAQS } from "@/lib/services";
import { whatsappLink } from "@/lib/site";
import clinicaImg from "@/assets/clinica-podologia-belem-para-interior.jpg";
import saudeImg from "@/assets/saude-dos-pes-belem-para.jpg";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: buildMeta({
      title: "Clínica de Podologia em Belém do Pará | Podomasso — Podóloga Rosa Chagas",
      description:
        "Clínica de podologia referência em Belém do Pará. Tratamento de unha encravada, calos, micose, pé diabético e mais com a podóloga Rosa Chagas. Agende já!",
      path: "/",
    }),
    links: buildLinks("/"),
    scripts: [faqSchema(FAQS)],
  }),
  component: HomePage,
});

function HomePage() {
  return (
    <>
      <Hero />

      {/* Sobre breve */}
      <section className="relative overflow-hidden bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <FadeUp>
              <div className="relative">
                <img
                  src={clinicaImg}
                  alt="Interior da Clínica Podomasso de Podologia em Belém do Pará"
                  loading="lazy"
                  width={1600}
                  height={1067}
                  className="aspect-[4/3] w-full rounded-3xl object-cover shadow-elegant"
                />
                <div className="absolute -bottom-6 -right-6 hidden h-32 w-32 rounded-3xl bg-gradient-hero shadow-glow sm:block" />
                <div className="absolute -bottom-4 -left-4 rounded-2xl bg-background/95 p-4 shadow-card backdrop-blur sm:p-5">
                  <p className="font-display text-3xl font-bold text-primary">
                    <CountUp to={10} suffix="+" />
                  </p>
                  <p className="text-xs font-medium text-muted-foreground">
                    Anos de Experiência
                  </p>
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.15}>
              <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">
                Quem Somos
              </p>
              <h2 className="font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
                Especialistas em Levar Alívio e Qualidade de Vida para Seus Pés
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                A <strong className="text-foreground">Podomasso Clínica de Podologia</strong> é
                referência em tratamentos podológicos avançados em Belém do Pará, com atendimento
                humanizado e personalizado. Sob a responsabilidade da podóloga{" "}
                <strong className="text-foreground">Rosa Chagas (CR8M 00017)</strong>, utilizamos
                técnicas modernas e equipamentos de última geração para cuidar da saúde dos seus
                pés.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Nosso compromisso é oferecer um ambiente acolhedor, limpo e seguro, onde cada
                paciente recebe atenção individualizada.
              </p>

              <div className="mt-8 grid grid-cols-2 gap-4">
                {[
                  { icon: ShieldCheck, label: "CR8M 00017", sub: "Podóloga Certificada" },
                  { icon: Users, label: "500+", sub: "Clientes Satisfeitos" },
                  { icon: Sparkles, label: "100%", sub: "Biossegurança Total" },
                  { icon: Heart, label: "10+", sub: "Anos de Experiência" },
                ].map((s) => (
                  <div
                    key={s.sub}
                    className="rounded-xl border border-border bg-gradient-card p-4 shadow-soft"
                  >
                    <s.icon className="mb-2 h-5 w-5 text-primary" />
                    <p className="font-display text-lg font-bold text-foreground">{s.label}</p>
                    <p className="text-xs text-muted-foreground">{s.sub}</p>
                  </div>
                ))}
              </div>
              <Button asChild variant="hero" size="lg" className="mt-8">
                <Link to="/sobre">Conheça nossa história</Link>
              </Button>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-gradient-hero py-16 text-primary-foreground lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <div className="mb-10 text-center">
              <p className="mb-2 text-sm font-semibold uppercase tracking-wider opacity-90">
                Nossa Trajetória
              </p>
              <h2 className="font-display text-3xl font-bold sm:text-4xl">
                Números que Comprovam Nossa Excelência
              </h2>
              <p className="mt-3 opacity-90">
                Mais de uma década cuidando dos pés dos paraenses com dedicação e técnica.
              </p>
            </div>
          </FadeUp>
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-3">
            {[
              { v: 500, s: "+", t: "Pacientes Atendidos", sub: "em Belém do Pará" },
              { v: 10, s: "+", t: "Anos de Experiência", sub: "em podologia clínica" },
              { v: 8, s: "", t: "Tratamentos Especializados", sub: "para a saúde dos pés" },
              { v: 98, s: "%", t: "Pacientes Satisfeitos", sub: "recomendam a Podomasso" },
              { v: 100, s: "%", t: "Biossegurança Total", sub: "Esterilização em autoclave" },
              { v: 4.9, s: "/5", t: "Avaliação no Google", sub: "Baseado em depoimentos" },
            ].map((s, i) => (
              <FadeUp key={s.t} delay={i * 0.08}>
                <div className="rounded-2xl bg-white/10 p-6 text-center backdrop-blur-sm">
                  <p className="font-display text-4xl font-bold sm:text-5xl">
                    <CountUp to={s.v} suffix={s.s} decimals={s.v === 4.9 ? 1 : 0} />
                  </p>
                  <p className="mt-2 text-sm font-semibold">{s.t}</p>
                  <p className="text-xs opacity-80">{s.sub}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Serviços */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <div className="mb-12 text-center">
              <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">
                Nossos Serviços
              </p>
              <h2 className="font-display text-3xl font-bold sm:text-4xl lg:text-5xl">
                Tratamentos Especializados em Podologia
              </h2>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                Oferecemos uma gama completa de tratamentos podológicos com técnicas modernas e
                equipamentos de última geração para garantir sua saúde e bem-estar duradouro.
              </p>
            </div>
          </FadeUp>
          <ServicesGrid />
          <div className="mt-12 text-center">
            <Button asChild variant="hero" size="lg">
              <Link to="/servicos">
                Ver Todos os Tratamentos <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Testimonials />

      {/* CTA */}
      <section className="relative overflow-hidden py-20 lg:py-28">
        <img
          src={saudeImg}
          alt="Saúde dos pés em Belém do Pará — Podomasso"
          loading="lazy"
          width={1600}
          height={900}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-primary/70" />
        <div className="relative mx-auto max-w-4xl px-4 text-center text-primary-foreground sm:px-6 lg:px-8">
          <FadeUp>
            <h2 className="font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
              Não Deixe para Amanhã o Cuidado que Seus Pés Merecem Hoje
            </h2>
            <p className="mx-auto mt-5 max-w-2xl opacity-95">
              Agende sua avaliação e descubra como podemos ajudar você a recuperar o conforto e a
              saúde dos seus pés. Atendimento personalizado e humanizado em Belém do Pará.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild variant="whatsapp" size="xl">
                <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
                  Agendar pelo WhatsApp
                </a>
              </Button>
              <Button asChild variant="soft" size="xl">
                <Link to="/agendamento">Formulário de agendamento</Link>
              </Button>
            </div>
            <ul className="mx-auto mt-8 flex max-w-2xl flex-wrap justify-center gap-x-8 gap-y-3 text-sm">
              {["Avaliação completa", "Atendimento humanizado", "Biossegurança rigorosa"].map(
                (b) => (
                  <li key={b} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary-glow" /> {b}
                  </li>
                ),
              )}
            </ul>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <div className="mb-12 text-center">
              <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-primary">
                Dúvidas Frequentes
              </p>
              <h2 className="font-display text-3xl font-bold sm:text-4xl lg:text-5xl">
                Perguntas Frequentes
              </h2>
            </div>
          </FadeUp>
          <FAQ />
        </div>
      </section>
    </>
  );
}
